// Reviews.jsx  (Employer side)
// Already imported in EmployerDashboard.jsx as:
//   import Reviews from './Reviews'
// No changes needed in EmployerDashboard — just replace this file.

import { useState, useEffect } from 'react'
import { get, patch, del } from '../lib/api'
import ReviewReport from './ReviewReport'

const EVIDENCE_TYPE_ICONS = { drive: '📁', pdf: '📄', image: '🖼️', link: '🔗' }

function scoreColor(s) { return s >= 8 ? '#059669' : s >= 6 ? '#4F46E5' : s >= 4 ? '#D97706' : '#EF4444' }
function scoreLabel(s) { return s >= 8 ? 'Outstanding' : s >= 6 ? 'Good' : s >= 4 ? 'Average' : 'Below Average' }

const STATUS_MAP = {
  submitted:         { bg: '#FFFBEB', color: '#D97706',  label: 'Awaiting Review',    icon: '⏳' },
  employer_reviewed: { bg: '#EFF6FF', color: '#4F46E5',  label: 'Awaiting Sign-off',  icon: '✍️' },
  signed_off:        { bg: '#ECFDF5', color: '#059669',  label: 'Signed Off',          icon: '✅' },
  disputed:          { bg: '#FFF7ED', color: '#D97706',  label: 'Disputed by Employee', icon: '⚠️' },
}

// ════════════════════════════════════════════════════════════════════════════
// ROOT — employee list
// ════════════════════════════════════════════════════════════════════════════
export default function Reviews({ profile, t }) {
  const [empReviews, setEmpReviews]   = useState([])
  const [loading, setLoading]         = useState(true)
  const [selectedEmp, setSelectedEmp] = useState(null)
  const [selectedReview, setSelectedReview] = useState(null)
  const [showReport, setShowReport]   = useState(false)
  const [reportData, setReportData]   = useState(null)  // { review, employee }
  const [deletingId, setDeletingId]   = useState(null)
  const [deleting, setDeleting]       = useState(false)

  useEffect(() => { fetchAll() }, [])

  async function fetchAll() {
    setLoading(true)
    const [usersRes, reviewsRes] = await Promise.all([
      get('/users'),
      get('/reviews'),
    ])
    const employees = (usersRes.users || []).filter(u => u.role === 'employee')
    const reviews   = reviewsRes.reviews || []
    const grouped = employees
      .map(emp => ({ emp, reviews: reviews.filter(r => r.employee_id === emp.id) }))
      .filter(g => g.reviews.length > 0)
      .sort((a, b) => b.reviews.filter(r => r.status === 'submitted').length - a.reviews.filter(r => r.status === 'submitted').length)
    setEmpReviews(grouped)
    setLoading(false)
  }

  async function handleDelete(reviewId) {
    setDeleting(true)
    try { await del(`/reviews/${reviewId}`) } catch (e) { console.error(e.message) }
    setDeletingId(null)
    setDeleting(false)
    setSelectedReview(null)
    fetchAll()
  }

  async function openReview(review) {
    try {
      const res = await get(`/reviews/${review.id}`)
      setSelectedReview(res.review)
    } catch (e) { console.error(e.message) }
  }

  if (showReport && reportData) return (
    <ReviewReport review={reportData.review} employee={reportData.employee}
      onClose={() => { setShowReport(false); setReportData(null) }} />
  )

  if (selectedEmp && selectedReview) {
    return (
      <ReviewScoring
        t={t}
        review={selectedReview}
        employee={selectedEmp}
        deletingId={deletingId}
        setDeletingId={setDeletingId}
        deleting={deleting}
        onDelete={handleDelete}
        onShowReport={(rev) => { setReportData({ review: rev, employee: selectedEmp }); setShowReport(true) }}
        onBack={() => setSelectedReview(null)}
        onDone={async () => {
          await fetchAll()
          const res = await get(`/reviews/${selectedReview.id}`)
          setSelectedReview(res.review)
        }}
      />
    )
  }

  if (selectedEmp) {
    const group = empReviews.find(g => g.emp.id === selectedEmp.id)
    return (
      <EmployeeReviewList
        t={t} employee={selectedEmp} reviews={group?.reviews || []}
        onBack={() => setSelectedEmp(null)}
        onOpen={r => openReview(r)}
        onDelete={handleDelete}
        onShowReport={(rev) => { setReportData({ review: rev, employee: selectedEmp }); setShowReport(true) }}
        deletingId={deletingId} setDeletingId={setDeletingId} deleting={deleting}
      />
    )
  }

  // ── EMPLOYEE LIST ──
  const totalPending = empReviews.reduce((s, g) => s + g.reviews.filter(r => r.status === 'submitted').length, 0)

  return (
    <div>
      <div style={{ marginBottom: '1.75rem' }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: t.text, margin: '0 0 4px', letterSpacing: '-0.5px' }}>Reviews</h1>
        <p style={{ fontSize: 14, color: t.textMuted, margin: 0 }}>Score and comment on employee performance review submissions</p>
      </div>

      {totalPending > 0 && (
        <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 12, padding: '12px 16px', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18 }}>⏳</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#D97706' }}>
            {totalPending} review{totalPending > 1 ? 's' : ''} waiting for your assessment
          </span>
        </div>
      )}

      {loading ? (
        <p style={{ color: t.textMuted }}>Loading…</p>
      ) : empReviews.length === 0 ? (
        <div style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '4rem', textAlign: 'center', boxShadow: t.shadow }}>
          <div style={{ fontSize: 52, marginBottom: '1rem' }}>⭐</div>
          <h3 style={{ fontSize: 20, fontWeight: 700, color: t.text, marginBottom: 8 }}>No reviews submitted yet</h3>
          <p style={{ fontSize: 14, color: t.textMuted }}>Employees will submit performance reviews against their active PDR goals. They'll appear here for you to score.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px,1fr))', gap: 14 }}>
          {empReviews.map(({ emp, reviews }) => {
            const pending    = reviews.filter(r => r.status === 'submitted').length
            const signOff    = reviews.filter(r => r.status === 'employer_reviewed').length
            const completed  = reviews.filter(r => r.status === 'signed_off').length
            const hasPending = pending > 0

            return (
              <div key={emp.id} onClick={() => setSelectedEmp(emp)}
                style={{ background: t.card, border: `2px solid ${hasPending ? '#FDE68A' : t.cardBorder}`, borderRadius: 14, padding: '1.25rem', boxShadow: t.shadow, cursor: 'pointer', position: 'relative' }}>

                {hasPending && (
                  <div style={{ position: 'absolute', top: -8, right: -8, width: 24, height: 24, borderRadius: '50%', background: '#F59E0B', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: '#fff', border: '2px solid white' }}>
                    {pending}
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1rem' }}>
                  <div style={{ width: 46, height: 46, borderRadius: '50%', background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                    {emp.full_name?.charAt(0)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: t.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{emp.full_name}</div>
                    <div style={{ fontSize: 12, color: t.textMuted }}>{emp.position || emp.email}</div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                  {pending > 0 && (
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 99, background: '#FFFBEB', color: '#D97706', border: '1px solid #FDE68A' }}>
                      ⏳ {pending} to review
                    </span>
                  )}
                  {signOff > 0 && (
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 99, background: '#EFF6FF', color: '#4F46E5', border: '1px solid #BFDBFE' }}>
                      ✍️ {signOff} sign-off pending
                    </span>
                  )}
                  {completed > 0 && (
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 99, background: '#ECFDF5', color: '#059669' }}>
                      ✅ {completed} done
                    </span>
                  )}
                </div>

                <div style={{ fontSize: 13, color: hasPending ? '#D97706' : t.primary, fontWeight: 600 }}>
                  {hasPending ? 'Review now →' : 'View reviews →'}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// EMPLOYEE REVIEW LIST — after clicking an employee
// ════════════════════════════════════════════════════════════════════════════
function EmployeeReviewList({ t, employee, reviews, onBack, onOpen, onDelete, onShowReport, deletingId, setDeletingId, deleting }) {
  return (
    <div>
      <button onClick={onBack} style={{ fontSize: 13, color: t.textMuted, background: 'none', border: 'none', cursor: 'pointer', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'Inter, system-ui' }}>
        ← Back to reviews
      </button>

      <div style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '1.25rem 1.5rem', marginBottom: '1.5rem', boxShadow: t.shadow, display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ width: 50, height: 50, borderRadius: '50%', background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
          {employee.full_name?.charAt(0)}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: t.text }}>{employee.full_name}</div>
          <div style={{ fontSize: 13, color: t.textMuted }}>{employee.position || employee.email}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 2 }}>Total reviews</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: t.primary }}>{reviews.length}</div>
        </div>
      </div>

      {reviews.map(review => {
        const s = STATUS_MAP[review.status] || STATUS_MAP.submitted
        const entries = review.review_goal_entries || []
        const avgScore = entries.filter(e => e.employer_score).length
          ? (entries.reduce((sum, e) => sum + (e.employer_score || 0), 0) / entries.filter(e => e.employer_score).length).toFixed(1)
          : null
        const isPending = review.status === 'submitted'
        const isSignedOff = review.status === 'signed_off'
        const isDeleting = deletingId === review.id

        return (
          <div key={review.id}
            style={{ background: t.card, border: `2px solid ${isPending ? '#FDE68A' : isSignedOff ? '#A7F3D0' : t.cardBorder}`, borderRadius: 14, padding: '1.25rem 1.5rem', marginBottom: 14, boxShadow: t.shadow }}>

            {/* Delete confirm */}
            {isDeleting && (
              <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '1rem 1.25rem', marginBottom: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#DC2626', marginBottom: 4 }}>⚠️ Delete this review?</div>
                <div style={{ fontSize: 13, color: '#7F1D1D', marginBottom: 12, lineHeight: 1.5 }}>
                  This will permanently delete the review submission and all goal entries. The employee will need to resubmit.
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => onDelete(review.id)} disabled={deleting}
                    style={{ padding: '8px 18px', background: '#EF4444', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, color: '#fff', cursor: 'pointer', fontFamily: 'Inter,system-ui' }}>
                    {deleting ? 'Deleting…' : 'Yes, delete permanently'}
                  </button>
                  <button onClick={() => setDeletingId(null)}
                    style={{ padding: '8px 18px', background: 'transparent', border: '1px solid #FECACA', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#DC2626', cursor: 'pointer', fontFamily: 'Inter,system-ui' }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, cursor: 'pointer' }} onClick={() => onOpen(review)}>
              <div>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: t.text, margin: '0 0 4px' }}>
                  {review.review_period || `Review ${review.year || ''}`}
                </h3>
                <div style={{ fontSize: 12, color: t.textMuted }}>
                  {entries.length} goals · Submitted {new Date(review.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
              </div>
              <span style={{ fontSize: 12, fontWeight: 600, background: s.bg, color: s.color, padding: '5px 14px', borderRadius: 99, border: `1px solid ${s.color}30` }}>
                {s.icon} {s.label}
              </span>
            </div>

            {avgScore && (
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 8, cursor: 'pointer' }} onClick={() => onOpen(review)}>
                <span style={{ fontSize: 22, fontWeight: 900, color: t.primary }}>{avgScore}</span>
                <span style={{ fontSize: 12, color: t.textMuted }}>/10 avg score given</span>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 10, borderTop: `1px solid ${t.border}`, marginTop: 8 }}>
              <button onClick={() => onOpen(review)}
                style={{ padding: '7px 14px', background: `${t.primary}14`, border: `1px solid ${t.primary}30`, borderRadius: 8, fontSize: 12, fontWeight: 600, color: t.primary, cursor: 'pointer', fontFamily: 'Inter,system-ui' }}>
                {isPending ? '⏳ Review now →' : 'View →'}
              </button>
              {isSignedOff && (
                <button onClick={() => onShowReport(review)}
                  style={{ padding: '7px 14px', background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 8, fontSize: 12, fontWeight: 600, color: '#059669', cursor: 'pointer', fontFamily: 'Inter,system-ui' }}>
                  📊 Full Report
                </button>
              )}
              {!isDeleting && (
                <button onClick={() => setDeletingId(review.id)}
                  style={{ padding: '7px 14px', background: 'transparent', border: '1px solid #FECACA', borderRadius: 8, fontSize: 12, fontWeight: 600, color: '#EF4444', cursor: 'pointer', fontFamily: 'Inter,system-ui', marginLeft: 'auto' }}>
                  🗑 Delete
                </button>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// REVIEW SCORING — employer scores each goal + final assessment
// ════════════════════════════════════════════════════════════════════════════
function ReviewScoring({ t, review, employee, onBack, onDone, onDelete, onShowReport, deletingId, setDeletingId, deleting }) {
  // Deduplicate by pdr_goal_id — keep latest entry per goal (old saves created duplicates)
  const entries = Object.values(
    (review.review_goal_entries || []).reduce((acc, e) => {
      const key = e.pdr_goal_id || e.goal_number
      if (!acc[key] || e.id > acc[key].id) acc[key] = e
      return acc
    }, {})
  ).sort((a, b) => a.goal_number - b.goal_number)
  const isReadOnly  = review.status === 'signed_off'
  const isDisputed  = review.status === 'disputed'

  const [scores, setScores]         = useState(() => Object.fromEntries(entries.map(e => [e.id, e.employer_score || ''])))
  const [comments, setComments]     = useState(() => Object.fromEntries(entries.map(e => [e.id, e.employer_comment || ''])))
  const [finalComment, setFinalComment]     = useState(review.employer_final_comment || '')
  const [improvementAreas, setImprovementAreas] = useState(review.improvement_areas || '')
  const [finalScore, setFinalScore] = useState(review.final_score || '')
  const [loading, setLoading]       = useState(false)
  const [error, setError]           = useState('')
  const [autoSaving, setAutoSaving] = useState(false)
  const [lastSaved, setLastSaved]   = useState(null)

  // Auto-save whenever scores, comments or final fields change
  useEffect(() => {
    if (isReadOnly) return
    const hasData = Object.values(scores).some(s => s) || Object.values(comments).some(c => c?.trim()) || finalComment
    if (!hasData) return
    const timer = setTimeout(async () => {
      setAutoSaving(true)
      for (const entry of entries) {
        if (scores[entry.id] !== undefined || comments[entry.id] !== undefined) {
          await patch(`/reviews/entries/${entry.id}`, {
            employer_score:   scores[entry.id] ? parseInt(scores[entry.id]) : null,
            employer_comment: comments[entry.id] || null,
          })
        }
      }
      if (finalComment || improvementAreas || finalScore) {
        await patch(`/reviews/${review.id}`, {
          employer_final_comment: finalComment || null,
          improvement_areas:      improvementAreas || null,
          final_score:            finalScore || null,
        })
      }
      setAutoSaving(false)
      setLastSaved(new Date())
    }, 1500)
    return () => clearTimeout(timer)
  }, [scores, comments, finalComment, improvementAreas, finalScore])

  const isSignedOff = review.status === 'signed_off'

  // Auto-calculate weighted final score
  const totalWeight = entries.reduce((s, e) => s + (e.weightage || 0), 0)
  const weightedScore = entries.every(e => scores[e.id])
    ? (entries.reduce((sum, e) => sum + (parseFloat(scores[e.id]) * (e.weightage / totalWeight)), 0)).toFixed(1)
    : null

  async function handleSubmit() {
    setError('')
    const missing = entries.filter(e => !scores[e.id] || !comments[e.id]?.trim())
    if (missing.length > 0) return setError(`Please score and comment on all ${missing.length} goal${missing.length > 1 ? 's' : ''} before submitting`)
    if (!finalComment.trim()) return setError('Please add an overall comment before submitting')

    setLoading(true)

    try {
      // Update each goal entry
      for (const entry of entries) {
        await patch(`/reviews/entries/${entry.id}`, {
          employer_score: parseInt(scores[entry.id]),
          employer_comment: comments[entry.id],
        })
      }

      // Update the review itself
      await patch(`/reviews/${review.id}`, {
        status:                  'employer_reviewed',
        employer_final_comment:  finalComment,
        improvement_areas:       improvementAreas,
        final_score:             finalScore || weightedScore,
        employer_reviewed_at:    new Date().toISOString(),
        manager_score:           finalScore || weightedScore, // keep legacy column in sync
        manager_notes:           finalComment,
      })
    } catch (e) { setError(e.message); setLoading(false); return }
    setLoading(false)
    onBack() // auto-return to employee's review list
  }

  const IS = { display: 'block', width: '100%', padding: '11px 14px', background: t.input, border: `1px solid ${t.inputBorder}`, borderRadius: 8, fontSize: 14, color: t.inputText, fontFamily: 'Inter, system-ui', boxSizing: 'border-box', outline: 'none' }
  const LS = { display: 'block', fontSize: 13, fontWeight: 600, color: t.text, marginBottom: 6 }

  return (
    <div>
      <button onClick={onBack} style={{ fontSize: 13, color: t.textMuted, background: 'none', border: 'none', cursor: 'pointer', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'Inter, system-ui' }}>
        ← Back to {employee.full_name}'s reviews
      </button>

      {/* Header */}
      <div style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '1.25rem 1.5rem', marginBottom: '1.5rem', boxShadow: t.shadow, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 46, height: 46, borderRadius: '50%', background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
            {employee.full_name?.charAt(0)}
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: t.text }}>{employee.full_name}</div>
            <div style={{ fontSize: 13, color: t.textMuted }}>{review.review_period || `Review ${review.year || ''}`}</div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          {(review.final_score || weightedScore) && (
            <div>
              <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 2 }}>{isReadOnly ? 'FINAL SCORE' : 'WEIGHTED SCORE'}</div>
              <div style={{ fontSize: 28, fontWeight: 900, color: scoreColor(parseFloat(review.final_score || weightedScore)) }}>
                {review.final_score || weightedScore}<span style={{ fontSize: 14, fontWeight: 400, color: t.textMuted }}>/10</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {isReadOnly && (
        <div style={{ background: isSignedOff ? '#ECFDF5' : '#EFF6FF', border: `1px solid ${isSignedOff ? '#A7F3D0' : '#BFDBFE'}`, borderRadius: 12, padding: '12px 16px', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>{isSignedOff ? '✅' : '✍️'}</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: isSignedOff ? '#059669' : '#4F46E5' }}>
              {isSignedOff ? 'This review has been signed off by the employee.' : 'Assessment submitted. Awaiting employee sign-off.'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {isSignedOff && (
              <button onClick={() => onShowReport(review)}
                style={{ padding: '7px 14px', background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 8, fontSize: 12, fontWeight: 700, color: '#059669', cursor: 'pointer', fontFamily: 'Inter,system-ui' }}>
                📊 Full Report
              </button>
            )}
            {deletingId === review.id ? (
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => onDelete(review.id)} disabled={deleting}
                  style={{ padding: '7px 14px', background: '#EF4444', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, color: '#fff', cursor: 'pointer', fontFamily: 'Inter,system-ui' }}>
                  {deleting ? 'Deleting…' : 'Confirm delete'}
                </button>
                <button onClick={() => setDeletingId(null)}
                  style={{ padding: '7px 14px', background: 'transparent', border: '1px solid #FECACA', borderRadius: 8, fontSize: 12, fontWeight: 600, color: '#EF4444', cursor: 'pointer', fontFamily: 'Inter,system-ui' }}>
                  Cancel
                </button>
              </div>
            ) : (
              <button onClick={() => setDeletingId(review.id)}
                style={{ padding: '7px 14px', background: 'transparent', border: '1px solid #FECACA', borderRadius: 8, fontSize: 12, fontWeight: 600, color: '#EF4444', cursor: 'pointer', fontFamily: 'Inter,system-ui' }}>
                🗑 Delete
              </button>
            )}
          </div>
        </div>
      )}

      {/* Per-goal scoring */}
      {entries.map((entry, i) => (
        <div key={entry.id} style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, marginBottom: 16, overflow: 'hidden', boxShadow: t.shadow }}>

          {/* Goal header */}
          <div style={{ background: `linear-gradient(135deg, #4F46E508 0%, #6366F105 100%)`, padding: '12px 20px', borderBottom: `1px solid ${t.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: '#fff' }}>{entry.goal_number}</div>
              <span style={{ fontSize: 15, fontWeight: 700, color: t.text }}>{entry.goal_title}</span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 12, fontWeight: 700, background: '#4F46E514', color: '#4F46E5', padding: '3px 10px', borderRadius: 99 }}>{entry.weightage}% weight</span>
              {scores[entry.id] && (
                <span style={{ fontSize: 14, fontWeight: 900, color: scoreColor(parseInt(scores[entry.id])) }}>
                  {scores[entry.id]}/10
                </span>
              )}
            </div>
          </div>

          <div style={{ padding: '1.5rem' }}>
            {/* What was planned */}
            <div style={{ background: t.dark ? 'rgba(255,255,255,0.04)' : '#F8F9FA', borderRadius: 8, padding: '10px 12px', marginBottom: '1rem' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>📋 What was planned</div>
              {entry.goal_description && <p style={{ fontSize: 13, color: t.text, margin: '0 0 4px', lineHeight: 1.6 }}>{entry.goal_description}</p>}
              {entry.kpi && <p style={{ fontSize: 13, color: t.textSub, margin: 0 }}><strong>KPI:</strong> {entry.kpi}</p>}
            </div>

            {/* What was done */}
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>✅ What the employee accomplished</div>
              <p style={{ fontSize: 14, color: t.text, lineHeight: 1.7, margin: 0 }}>{entry.what_was_done}</p>
            </div>

            {/* Employee's self-rating (visible to employer for reference) */}
            {entry.employee_score != null && entry.employee_score !== '' && (
              <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: 10, background: `${scoreColor(entry.employee_score)}0D`, border: `1px solid ${scoreColor(entry.employee_score)}33`, borderRadius: 10, padding: '10px 14px' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>🙋 Employee self-rating</span>
                <span style={{ fontSize: 16, fontWeight: 900, color: scoreColor(entry.employee_score) }}>{entry.employee_score}/10</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: scoreColor(entry.employee_score) }}>{scoreLabel(entry.employee_score)}</span>
              </div>
            )}

            {/* Evidence */}
            {(entry.evidence_links || []).filter(ev => ev.url).length > 0 && (
              <div style={{ marginBottom: '1.25rem' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>📁 Evidence submitted</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {entry.evidence_links.filter(ev => ev.url).map((ev, ei) => (
                    <a key={ei} href={ev.url} target="_blank" rel="noreferrer"
                      style={{ fontSize: 12, fontWeight: 600, background: '#EFF6FF', color: '#4F46E5', padding: '7px 14px', borderRadius: 8, textDecoration: 'none', border: '1px solid #BFDBFE', display: 'flex', alignItems: 'center', gap: 6 }}>
                      {EVIDENCE_TYPE_ICONS[ev.type] || '🔗'} {ev.label || 'View evidence'}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Score + comment — or read-only if already reviewed */}
            {isReadOnly ? (
              <div style={{ background: '#F0F0FF', border: '1px solid #C7D2FE', borderRadius: 10, padding: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#4F46E5', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Your comment</div>
                    <p style={{ fontSize: 14, color: t.text, margin: 0, lineHeight: 1.7 }}>{entry.employer_comment || '—'}</p>
                  </div>
                  {entry.employer_score && (
                    <div style={{ textAlign: 'center', flexShrink: 0, background: `${scoreColor(entry.employer_score)}10`, borderRadius: 10, padding: '10px 16px', border: `1px solid ${scoreColor(entry.employer_score)}30` }}>
                      <div style={{ fontSize: 28, fontWeight: 900, color: scoreColor(entry.employer_score), lineHeight: 1 }}>{entry.employer_score}</div>
                      <div style={{ fontSize: 10, color: t.textMuted }}>/10</div>
                      <div style={{ fontSize: 10, fontWeight: 600, color: scoreColor(entry.employer_score) }}>{scoreLabel(entry.employer_score)}</div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ background: '#F0F0FF', border: '1px solid #C7D2FE', borderRadius: 10, padding: '1rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: 14, alignItems: 'start' }}>
                  <div>
                    <label style={{ ...LS, color: '#4F46E5' }}>Your comment on this goal *</label>
                    <textarea value={comments[entry.id]}
                      onChange={e => setComments(prev => ({ ...prev, [entry.id]: e.target.value }))}
                      placeholder="Provide specific, constructive feedback on this goal's performance…"
                      rows={3}
                      style={{ ...IS, resize: 'vertical', background: t.card }} />
                  </div>
                  <div>
                    <label style={{ ...LS, color: '#4F46E5' }}>Score (1–10) *</label>
                    {/* Score buttons */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 4, marginBottom: 8 }}>
                      {[1,2,3,4,5,6,7,8,9,10].map(n => (
                        <button key={n} onClick={() => setScores(prev => ({ ...prev, [entry.id]: n }))}
                          style={{ padding: '8px 4px', borderRadius: 6, border: `2px solid ${scores[entry.id] === n ? scoreColor(n) : t.border}`, background: scores[entry.id] === n ? `${scoreColor(n)}14` : t.card, color: scores[entry.id] === n ? scoreColor(n) : t.textMuted, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter, system-ui' }}>
                          {n}
                        </button>
                      ))}
                    </div>
                    {scores[entry.id] && (
                      <div style={{ fontSize: 12, fontWeight: 700, color: scoreColor(scores[entry.id]), textAlign: 'center' }}>
                        {scoreLabel(scores[entry.id])}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ))}

      {/* Final assessment */}
      <div style={{ background: t.card, border: `2px solid ${t.primary}30`, borderRadius: 14, padding: '1.5rem', marginBottom: '1.5rem', boxShadow: t.shadow }}>
        <h3 style={{ fontSize: 16, fontWeight: 800, color: t.text, margin: '0 0 1.5rem' }}>📋 Overall Assessment</h3>

        {/* Weighted score display */}
        {weightedScore && !isReadOnly && (
          <div style={{ background: `${scoreColor(parseFloat(weightedScore))}10`, border: `1px solid ${scoreColor(parseFloat(weightedScore))}30`, borderRadius: 10, padding: '1rem 1.25rem', marginBottom: '1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, marginBottom: 2 }}>Weighted score (auto-calculated from goals)</div>
              <div style={{ fontSize: 13, color: t.textSub }}>You can override this below</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 32, fontWeight: 900, color: scoreColor(parseFloat(weightedScore)), lineHeight: 1 }}>{weightedScore}</div>
              <div style={{ fontSize: 11, color: t.textMuted }}>/10</div>
            </div>
          </div>
        )}

        {isReadOnly ? (
          <div>
            {review.final_score && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: '1.25rem', padding: '1rem', background: `${scoreColor(review.final_score)}10`, borderRadius: 10, border: `1px solid ${scoreColor(review.final_score)}30` }}>
                <div>
                  <div style={{ fontSize: 11, color: t.textMuted }}>FINAL SCORE</div>
                  <div style={{ fontSize: 36, fontWeight: 900, color: scoreColor(review.final_score) }}>
                    {review.final_score}<span style={{ fontSize: 16, fontWeight: 400 }}>/10</span>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: scoreColor(review.final_score) }}>{scoreLabel(review.final_score)}</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ background: t.border, borderRadius: 99, height: 10 }}>
                    <div style={{ width: `${review.final_score * 10}%`, height: '100%', background: scoreColor(review.final_score), borderRadius: 99 }} />
                  </div>
                </div>
              </div>
            )}
            {review.employer_final_comment && (
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, marginBottom: 6 }}>OVERALL COMMENT</div>
                <p style={{ fontSize: 14, color: t.text, margin: 0, lineHeight: 1.7 }}>{review.employer_final_comment}</p>
              </div>
            )}
            {review.improvement_areas && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#D97706', marginBottom: 6 }}>AREAS FOR IMPROVEMENT</div>
                <p style={{ fontSize: 14, color: t.text, margin: 0, lineHeight: 1.7 }}>{review.improvement_areas}</p>
              </div>
            )}
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={LS}>Overall comment *</label>
              <textarea value={finalComment} onChange={e => setFinalComment(e.target.value)}
                placeholder="Summarise the employee's overall performance this cycle. Be specific about strengths and areas to build on…"
                rows={4}
                style={{ ...IS, resize: 'vertical' }} />
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={LS}>Areas for improvement</label>
              <textarea value={improvementAreas} onChange={e => setImprovementAreas(e.target.value)}
                placeholder="Specific skills, behaviours or focus areas for the next review cycle…"
                rows={3}
                style={{ ...IS, resize: 'vertical' }} />
            </div>

            <div>
              <label style={LS}>Final score override (optional — leave blank to use weighted score)</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10,1fr)', gap: 6 }}>
                {[1,2,3,4,5,6,7,8,9,10].map(n => (
                  <button key={n} onClick={() => setFinalScore(finalScore === n ? '' : n)}
                    style={{ padding: '10px 4px', borderRadius: 8, border: `2px solid ${finalScore === n ? scoreColor(n) : t.border}`, background: finalScore === n ? `${scoreColor(n)}14` : t.card, color: finalScore === n ? scoreColor(n) : t.textMuted, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter, system-ui' }}>
                    {n}
                  </button>
                ))}
              </div>
              {finalScore && (
                <div style={{ marginTop: 8, fontSize: 13, fontWeight: 700, color: scoreColor(finalScore) }}>
                  {scoreLabel(finalScore)} ({finalScore}/10)
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Employee sign-off section (read-only view for employer) */}
      {isSignedOff && review.employee_sign_off_comment && (
        <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 14, padding: '1.25rem 1.5rem', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#059669', marginBottom: 6 }}>EMPLOYEE SIGN-OFF COMMENT</div>
          <p style={{ fontSize: 14, color: t.text, margin: 0, lineHeight: 1.7 }}>{review.employee_sign_off_comment}</p>
          <div style={{ fontSize: 12, color: '#059669', marginTop: 8 }}>
            Signed off on {new Date(review.signed_off_at).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
        </div>
      )}

      {/* Submit */}
      {!isReadOnly && (
        <div>
          {error && (
            <div style={{ fontSize: 13, color: '#EF4444', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
              ⚠️ {error}
            </div>
          )}
          <div style={{ background: `#4F46E508`, border: '1px solid #4F46E520', borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1rem' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4 }}>
              <p style={{ fontSize: 13, color: t.textSub, margin: 0, lineHeight: 1.7, flex:1 }}>
                Once submitted, the employee will be notified and asked to sign off. You will not be able to edit scores or comments after submission.
              </p>
              <div style={{ flexShrink:0, marginLeft:12, fontSize:12, color:t.textMuted, textAlign:'right' }}>
                {autoSaving && <span>💾 Saving…</span>}
                {!autoSaving && lastSaved && <span style={{ color:'#10B981' }}>✓ Auto-saved {lastSaved.toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'})}</span>}
              </div>
            </div>
          </div>
          <button onClick={handleSubmit} disabled={loading}
            style={{ width: '100%', padding: '14px', background: loading ? t.border : 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, color: loading ? t.textMuted : '#fff', cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'Inter, system-ui', boxShadow: loading ? 'none' : '0 2px 8px rgba(79,70,229,0.3)' }}>
            {loading ? 'Submitting assessment…' : '🚀 Submit Assessment to Employee'}
          </button>
        </div>
      )}
    </div>
  )
}
